import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useToast } from '../contexts/ToastContext';
import { fmtDate } from '../utils/formatUtils';
import { t } from '../i18n/nl.js';
import ConfirmModal from './ConfirmModal';

// Aantal oefeningnamen dat als preview op de kaart past zonder dat de
// kaarthoogtes in het raster uiteen gaan lopen.
const PREVIEW_MAX = 3;

export default function ClientTemplatesPanel({ clientId, readOnly = false }) {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [templates, setTemplates] = useState([]);
  const [laatstGebruikt, setLaatstGebruikt] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusConfirm, setStatusConfirm] = useState(null); // { id, name, naarStatus }
  const [bezig, setBezig] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [templatesRes, sessiesRes] = await Promise.all([
        supabase
          .from('client_exercise_templates')
          .select('id, name, status, created_at, valid_until, template_exercise_items(id, sort_order, exercises(name))')
          .eq('client_id', clientId)
          .order('created_at', { ascending: false })
          .order('id', { ascending: false }),
        // "Laatst gebruikt" blijft leeg zolang deel 4 (live loggen) niet
        // bestaat: er zijn dan simpelweg geen workout_sessions met een
        // template_id. Bewust wel al opgehaald, zodat de kaart meteen klopt
        // zodra dat deel live gaat.
        supabase
          .from('workout_sessions')
          .select('template_id, completed_at, created_at')
          .eq('client_id', clientId)
          .not('template_id', 'is', null),
      ]);

      if (templatesRes.error) throw templatesRes.error;
      if (sessiesRes.error) throw sessiesRes.error;

      setTemplates(templatesRes.data || []);

      const perTemplate = {};
      for (const sessie of sessiesRes.data || []) {
        const moment = sessie.completed_at || sessie.created_at;
        if (!moment) continue;
        const huidig = perTemplate[sessie.template_id];
        if (!huidig || new Date(moment) > new Date(huidig)) {
          perTemplate[sessie.template_id] = moment;
        }
      }
      setLaatstGebruikt(perTemplate);
    } catch (err) {
      showError(t('trainingTemplates.foutLaden') + err.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, showError]);

  useEffect(() => { load(); }, [load]);

  async function handleStatusWijzigen(id, naarStatus) {
    setBezig(true);
    try {
      // Rij-check: dit is een directe UPDATE (geen RPC), dus een policy die de
      // rij niet selecteert laat de call gewoon slagen met 0 geraakte rijen —
      // het paneel meldde dan "gearchiveerd" en herlaadde de oude status.
      const { data: updRow, error } = await supabase
        .from('client_exercise_templates')
        .update({ status: naarStatus })
        .eq('id', id)
        .select('id')
        .maybeSingle();
      if (error) throw error;
      if (!updRow) { showError(t('common.wijzigingNietOpgeslagen')); return; }
      setStatusConfirm(null);
      showSuccess(naarStatus === 'gearchiveerd'
        ? t('trainingTemplates.succesGearchiveerd')
        : t('trainingTemplates.succesTeruggezet'));
      await load();
    } catch (err) {
      showError(t('trainingTemplates.foutStatus') + err.message);
    } finally {
      setBezig(false);
    }
  }

  // Actieve sjablonen eerst; daarbinnen op naam. De gearchiveerde blijven
  // zichtbaar maar zakken naar onderen.
  const gesorteerd = [...templates].sort((a, b) => {
    if (a.status !== b.status) return a.status === 'actief' ? -1 : 1;
    return a.name.localeCompare(b.name, 'nl');
  });

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{t('trainingTemplates.titel')}</h2>
          <p className="text-muted" style={{ fontSize: 13, marginTop: 2 }}>{t('trainingTemplates.subtitel')}</p>
        </div>
        {!readOnly && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => navigate(`/coach/clients/${clientId}/sjabloon/nieuw`)}
          >
            {t('trainingTemplates.nieuw')}
          </button>
        )}
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : gesorteerd.length === 0 ? (
        <div className="empty-state">
          <div style={{ fontSize: 32 }}>🏋️</div>
          <p>{t('trainingTemplates.geenSjablonen')}</p>
        </div>
      ) : (
        <div className="template-cards">
          {gesorteerd.map(tmpl => {
            const items = [...(tmpl.template_exercise_items || [])]
              .sort((a, b) => a.sort_order - b.sort_order);
            const namen = items.map(i => i.exercises?.name).filter(Boolean);
            const gearchiveerd = tmpl.status === 'gearchiveerd';
            const gebruikt = laatstGebruikt[tmpl.id];
            return (
              <article key={tmpl.id} className={`template-card${gearchiveerd ? ' is-gearchiveerd' : ''}`}>
                <div className="template-card-head">
                  <h3>{tmpl.name}</h3>
                  <span className={`badge ${gearchiveerd ? 'badge-inactive' : 'badge-active'}`}>
                    {gearchiveerd ? t('trainingTemplates.statusGearchiveerd') : t('trainingTemplates.statusActief')}
                  </span>
                </div>

                <p className="template-card-meta">
                  {t('trainingTemplates.aantalOefeningen', { aantal: items.length })}
                  {' · '}
                  {gebruikt
                    ? t('trainingTemplates.laatstGebruikt', { datum: fmtDate(gebruikt) })
                    : t('trainingTemplates.nooitGebruikt')}
                </p>

                {tmpl.valid_until && (
                  <p className="template-card-meta">
                    {t('trainingTemplates.geldigTot', { datum: fmtDate(tmpl.valid_until) })}
                  </p>
                )}

                {namen.length > 0 && (
                  <ul className="template-card-preview">
                    {namen.slice(0, PREVIEW_MAX).map((naam, idx) => (
                      <li key={`${tmpl.id}-${idx}`}>{naam}</li>
                    ))}
                    {namen.length > PREVIEW_MAX && (
                      <li className="template-card-preview-rest">
                        {t('trainingTemplates.enNogMeer', { aantal: namen.length - PREVIEW_MAX })}
                      </li>
                    )}
                  </ul>
                )}

                {!readOnly && (
                  <div className="template-card-actions">
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => navigate(`/coach/clients/${clientId}/sjabloon/${tmpl.id}`)}
                    >
                      {t('trainingTemplates.bewerken')}
                    </button>
                    {gearchiveerd ? (
                      <button
                        className="btn btn-ghost btn-sm"
                        disabled={bezig}
                        onClick={() => handleStatusWijzigen(tmpl.id, 'actief')}
                      >
                        {t('trainingTemplates.terugzetten')}
                      </button>
                    ) : (
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => setStatusConfirm({ id: tmpl.id, name: tmpl.name })}
                      >
                        {t('trainingTemplates.archiveren')}
                      </button>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      {statusConfirm && (
        <ConfirmModal
          title={t('trainingTemplates.archiveerTitel')}
          body={t('trainingTemplates.archiveerTekst', { naam: statusConfirm.name })}
          confirmLabel={t('trainingTemplates.archiveren')}
          confirmDisabled={bezig}
          onConfirm={() => handleStatusWijzigen(statusConfirm.id, 'gearchiveerd')}
          onClose={() => setStatusConfirm(null)}
        />
      )}
    </div>
  );
}
